/*
 * File: /src/util/plainDate.js
 * Author: AdBase, Inc.
 * -----
 * Copyright (c) 2026 AdBase, Inc.
 *
 * AI generated util using Temporal instead of Date
 *
 * Date only values. They carry no time and no zone, so they are held as a
 * plain 'YYYY-MM-DD' string and carried around as a Temporal.PlainDate. A JS
 * Date always sits at an instant in a zone, which moves the day either side
 * of midnight, and `new Date('2026-08-21')` lands on UTC midnight and reads
 * back as the 20th west of Greenwich. PlainDate has no zone to shift
 */
import { Temporal } from 'temporal-polyfill';

/**
 * Read a stored value into a PlainDate. Takes the date part of a longer
 * string and the local day off a Date. Anything unreadable comes back
 * undefined rather than throwing, the value is whatever the API last held
 */
export function toPlainDate(value) {
  if (!value) {
    return undefined;
  }
  if (value instanceof Temporal.PlainDate) {
    return value;
  }
  try {
    if (typeof value === 'string') {
      return Temporal.PlainDate.from(value.slice(0, 10));
    }
    if (value instanceof Date) {
      if (isNaN(value.getTime())) {
        return undefined;
      }
      // The day on the clock in front of the user, not the UTC day
      return new Temporal.PlainDate(
        value.getFullYear(),
        value.getMonth() + 1,
        value.getDate()
      );
    }
    // A Temporal type that knows its own calendar day
    if (typeof value.toPlainDate === 'function') {
      return value.toPlainDate();
    }
  } catch (e) {
    return undefined;
  }
  return undefined;
}

/**
 * What gets stored and sent to the API, 'YYYY-MM-DD', or null so a patch
 * carries the key and clears it
 */
export function toDateOnlyString(value) {
  const plain = toPlainDate(value);
  return plain ? plain.toString() : null;
}

/**
 * The pickers are built on a JS Date, so hand them one standing on the day
 * itself. Built from the parts rather than parsed, parsing is where the zone
 * creeps back in
 */
export function toPickerDate(value) {
  const plain = toPlainDate(value);
  if (!plain) {
    return undefined;
  }
  return new Date(plain.year, plain.month - 1, plain.day);
}
